import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, FileText, DollarSign, Download } from "lucide-react";
import { getFormPackageById } from "../services/formPackagesService";
import { useAuth } from "../context/AuthContext";
import api from "../../utils/api";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

const FormPackageDetails = () => {
  const { package_id, lang } = useParams();
  const currentLang = lang || 'en';
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchPackage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getFormPackageById(package_id);
        setPackageData(data);
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Failed to load package details.";
        setError(errorMessage);
        Swal.fire("Error", errorMessage, "error");
      } finally {
        setLoading(false);
      }
    };

    if (package_id) {
      fetchPackage();
    } else {
      setError("Package ID is missing");
      setLoading(false);
    }
  }, [package_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading package details...</p>
        </div>
      </div>
    );
  }

  if (error && !packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Package Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/${currentLang}/forms`)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return null;
  }

  const price = packageData.price ? parseFloat(packageData.price) : 0;
  const savingPrice = packageData.saving_price ? parseFloat(packageData.saving_price) : 0;
  const forms = packageData.forms || [];

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      Swal.fire({
        title: "Login Required",
        text: "Please sign in to your account to purchase this package.",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Login Now",
        confirmButtonColor: "#2563eb",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(`/${currentLang}/login`);
        }
      });
      return;
    }

    if (!packageData.id) {
      Swal.fire("Error", "Package information is missing. Please try again later.", "error");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await api.post(`/payments/package/${packageData.id}/create-checkout`);
      if (response.data.success && response.data.data.checkout_url) {
        window.location.href = response.data.data.checkout_url;
      } else {
        Swal.fire("Error", response.data.message || "Failed to initiate payment.", "error");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const message = error.response?.data?.message || "Something went wrong. Please try again.";
      if (message.toLowerCase().includes("already own")) {
        Swal.fire("Notice", message, "warning");
      } else {
        Swal.fire("Payment Error", message, "error");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/${currentLang}/forms`)}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Forms
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-xl overflow-hidden"
        >
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Package className="w-8 h-8" />
                  <h1 className="text-3xl font-bold">{packageData.name}</h1>
                </div>
                {packageData.slug && (
                  <p className="text-blue-100 text-sm mt-1">Slug: {packageData.slug}</p>
                )}
              </div>
              <div className="text-right">
                {price > 0 ? (
                  <div>
                    <div className="text-3xl font-bold mb-1">${price.toFixed(2)}</div>
                    {savingPrice > 0 && (
                      <div className="text-sm text-green-200">
                        Save ${savingPrice.toFixed(2)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-2xl font-bold">Free</div>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Description */}
            {packageData.description && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Description</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {packageData.description}
                </p>
              </div>
            )}

            {/* Package Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Forms Included</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {packageData.forms_count || forms.length}
                    </p>
                  </div>
                </div>
              </div>

              {price > 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Price</p>
                      <p className="text-2xl font-bold text-gray-900">${price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {savingPrice > 0 && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <DollarSign className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">You Save</p>
                      <p className="text-2xl font-bold text-gray-900">${savingPrice.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Forms List */}
            {forms.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Forms in this Package ({forms.length})
                </h2>
                <div className="space-y-3">
                  {forms.map((form, index) => (
                    <motion.div
                      key={form.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <h3 className="font-semibold text-gray-900">{form.name}</h3>
                          </div>
                          {form.code && (
                            <p className="text-sm text-gray-600 mb-2">Code: {form.code}</p>
                          )}
                          {form.price && parseFloat(form.price) > 0 && (
                            <p className="text-sm font-medium text-gray-700">
                              Individual Price: ${parseFloat(form.price).toFixed(2)}
                            </p>
                          )}
                        </div>
                        {form.pdf_download_url && (
                          <a
                            href={form.pdf_download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {forms.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No forms available in this package.</p>
              </div>
            )}

            {/* Get Started Button */}
            {price > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <button
                    onClick={handleCheckout}
                    disabled={isProcessing}
                    className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl px-20 py-5 rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? "Processing..." : "Get Started Now"}
                  </button>
                  <p className="text-sm text-gray-500 mt-3 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure Checkout
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FormPackageDetails;
